import React, { useState } from 'react';
import Icon from '@ant-design/icons';
import './Comments.scss';
import { CommentBubbleIcon } from './commentIcons';
import { BaseButton } from '../../../../components/UI/BaseButton';
import { TaskComment } from '../../../../types/Comment';
import { Comment } from './Comment';
import { RichTextEditor } from '../editor/RichTextEditor';
import { useGetUserFromAccessToken } from '../../../../common/auth';

type CommentsProps = {
  comments?: TaskComment[];
  loading: boolean;
  onEditComment?: (commentId: number, content: string) => void;
  onDeleteComment?: (commentId: number) => void;
  onReplySubmit?: (content: string, parentId: number) => void | Promise<void>;
  usersWithAccessToSpace?: Array<{
    id: number;
    fullName?: string | null;
    email: string;
    profilePicturePath?: string | null;
  }>;
};

const byCreatedAt = (a: TaskComment, b: TaskComment) =>
  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

const Comments: React.FC<CommentsProps> = ({
  comments = [],
  loading,
  onEditComment,
  onDeleteComment,
  onReplySubmit,
  usersWithAccessToSpace = [],
}) => {
  const user = useGetUserFromAccessToken().data?.user;
  const [openThreadId, setOpenThreadId] = useState<number | null>(null);
  const [replyValue, setReplyValue] = useState('');

  const viewer = user ? { id: user.id.toString(), email: user.email, fullName: user.fullName } : undefined;

  // One-level threads: roots have no parentId; replies group under their parent.
  const roots = comments.filter((c) => !c.parentId).sort(byCreatedAt);
  const repliesByParent = new Map<number, TaskComment[]>();
  for (const c of comments) {
    if (c.parentId) {
      const list = repliesByParent.get(c.parentId) ?? [];
      list.push(c);
      repliesByParent.set(c.parentId, list);
    }
  }

  const openThread = (rootId: number) => {
    setOpenThreadId(rootId);
    setReplyValue('');
  };

  const closeThread = () => {
    setOpenThreadId(null);
    setReplyValue('');
  };

  const submitReply = async (rootId: number) => {
    if (replyValue && onReplySubmit) {
      await onReplySubmit(replyValue, rootId);
    }
    closeThread();
  };

  if (loading) {
    return <div className="py-4 text-xs text-neutral-800">Loading comments…</div>;
  }

  return (
    <div className="flex flex-col gap-3">
      {roots.map((root) => {
        const replies = (repliesByParent.get(root.id) ?? []).sort(byCreatedAt);
        const hasThread = replies.length > 0;
        return (
          <div key={root.id} className="flex flex-col gap-2">
            <Comment
              comment={root}
              viewer={viewer}
              onEdit={onEditComment}
              onDelete={onDeleteComment}
              onReply={() => openThread(root.id)}
              usersWithAccessToSpace={usersWithAccessToSpace}
            />

            {(hasThread || openThreadId === root.id) && (
              <div className="ml-6 flex flex-col gap-2 border-l border-neutral-200 pl-3">
                {replies.map((reply) => (
                  <Comment
                    key={reply.id}
                    comment={reply}
                    viewer={viewer}
                    onEdit={onEditComment}
                    onDelete={onDeleteComment}
                    onReply={() => openThread(root.id)}
                    usersWithAccessToSpace={usersWithAccessToSpace}
                  />
                ))}

                {openThreadId === root.id ? (
                  <RichTextEditor
                    value={replyValue}
                    onChange={setReplyValue}
                    onSubmit={() => void submitReply(root.id)}
                    onBlur={closeThread}
                    usersWithAccessToSpace={usersWithAccessToSpace}
                    showSubmitButton={true}
                    submitButtonText="Reply"
                    showCancelButton={true}
                    cancelButtonText="Cancel"
                    onCancel={closeThread}
                    placeholder="Reply to thread…"
                    mode="comment"
                    minHeight="60px"
                    autoFocus={true}
                  />
                ) : (
                  <BaseButton type="secondary" size="small" onClick={() => openThread(root.id)} className="w-fit">
                    <Icon component={CommentBubbleIcon} />
                    Add Comment to Thread
                  </BaseButton>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export { Comments };
